import * as React from 'react';
import { visualize, story, snap } from 'storybook-snapper';
import { storiesOf } from '@storybook/react';
import { VisualTestContainer } from '../../test/visual/VisualTestContainer';
import {
  TextField,
  TextFieldLabelStyle,
  TextFieldProps,
  TextFieldTheme,
} from '..';
import { TextFieldAsyncVisual } from './TextFieldAsyncVisual';
import { Calendar, LikeHeart, Info } from '@wix/wix-ui-icons-common/on-stage';
import { classes } from './TextField.visual.st.css';
import { ErrorAppearance } from '../internal/ErrorMessageWrapper';

class TextFieldVisual extends React.Component<TextFieldProps> {
  static defaultProps: TextFieldProps = {
    placeholder: 'Placeholder',
    onChange: () => {},
    value:
      'Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.',
  };

  render() {
    return (
      <VisualTestContainer>
        <TextField {...this.props} />
        {this.props.newErrorMessage && (
          <div className={classes.elementBelow}>
            An element below to show the spacing
          </div>
        )}
      </VisualTestContainer>
    );
  }
}

interface Test {
  describe: string;
  its: {
    it: string;
    props: Partial<TextFieldProps>;
  }[];
}

const tests: Test[] = [
  {
    describe: 'TextField',
    its: [
      {
        it: 'Placeholder',
        props: {
          placeholder: 'Placeholder',
          value: '',
        },
      },
      {
        it: 'Placeholder with styles override',
        props: {
          placeholder: 'Placeholder',
          value: '',
          className: classes.root,
        },
      },
      {
        it: 'Disabled placeholder',
        props: {
          placeholder: 'Placeholder',
          value: '',
          disabled: true,
        },
      },
      {
        it: 'Disabled placeholder with styles override',
        props: {
          placeholder: 'Placeholder',
          value: '',
          disabled: true,
          className: classes.root,
        },
      },
      {
        it: 'Value',
        props: {
          value: 'This is a value',
          maxLength: 50,
          showCharCount: true,
        },
      },
      {
        it: 'Value with style overrides',
        props: {
          label: 'Label',
          value: 'This is a value',
          maxLength: 50,
          showCharCount: true,
          className: classes.root,
        },
      },
      {
        it: 'Error',
        props: {
          error: true,
        },
      },
      {
        it: 'Error with error message',
        props: {
          error: true,
          errorMessage: 'This is an error message',
        },
      },
      {
        it: 'New error message without a message',
        props: {
          error: true,
          newErrorMessage: true,
        },
      },
      {
        it: 'New error message without a message / ErrorAppearance - BackgroundAndBorder',
        props: {
          errorAppearance: ErrorAppearance.BackgroundAndBorder,
          error: true,
          newErrorMessage: true,
        },
      },
      {
        it: 'New error message with message',
        props: {
          error: true,
          errorMessage: 'This is an error message',
          newErrorMessage: true,
        },
      },
      {
        it: 'New error message with message / ErrorAppearance - BackgroundAndBorder',
        props: {
          errorAppearance: ErrorAppearance.BackgroundAndBorder,
          error: true,
          errorMessage: 'This is an error message',
          newErrorMessage: true,
        },
      },
      {
        it: 'New error message with override styles',
        props: {
          error: true,
          newErrorMessage: true,
          className: classes.root,
        },
      },
      {
        it: 'New error message with override styles / ErrorAppearance - BackgroundAndBorder',
        props: {
          errorAppearance: ErrorAppearance.BackgroundAndBorder,
          error: true,
          newErrorMessage: true,
          className: classes.root,
        },
      },
      {
        it: 'New error message with message and override styles',
        props: {
          error: true,
          errorMessage: 'This is an error message',
          newErrorMessage: true,
          className: classes.root,
        },
      },
      {
        it: 'New error message with message and override styles / ErrorAppearance - BackgroundAndBorder',
        props: {
          errorAppearance: ErrorAppearance.BackgroundAndBorder,
          error: true,
          errorMessage: 'This is an error message',
          newErrorMessage: true,
          className: classes.root,
        },
      },
      {
        it: 'Success',
        props: {
          success: true,
        },
      },
      {
        it: 'Disabled',
        props: {
          disabled: true,
        },
      },
      {
        it: 'Disabled with style overrides',
        props: {
          disabled: true,
          className: classes.root,
        },
      },
      {
        it: 'Prefix text',
        props: {
          prefix: '$',
        },
      },
      {
        it: 'Prefix icon',
        props: {
          prefix: <LikeHeart />,
        },
      },
      {
        it: 'Prefix icon with style overrides',
        props: {
          prefix: <LikeHeart />,
          className: classes.root,
        },
      },
      {
        it: 'Prefix label',
        props: {
          prefix: '$',
          prefixLabel: 'USD',
        },
      },
      {
        it: 'Prefix label with style overrides',
        props: {
          prefix: '$',
          prefixLabel: 'USD',
          className: classes.root,
        },
      },
      {
        it: 'Force label',
        props: {
          forceLabel: true,
        },
      },
      {
        it: 'Up & down arrows when type is number',
        props: {
          type: 'number',
          value: '0',
        },
      },
      {
        it: 'Password',
        props: {
          type: 'password',
        },
      },
      {
        it: 'Password large',
        props: {
          type: 'password',
          prefix: <Info />,
          suffix: <LikeHeart />,
          className: classes.largeText,
        },
      },
      {
        it: 'Password huge',
        props: {
          type: 'password',
          prefix: <Info />,
          suffix: <LikeHeart />,
          className: classes.hugeText,
        },
      },
      {
        it: 'Password with helperText',
        props: {
          type: 'password',
          helperText: 'Helper text',
          label: 'Label',
        },
      },
      {
        it: 'Password disabled',
        props: {
          type: 'password',
          label: 'Label',
          disabled: true,
        },
      },
      {
        it: 'Password with error',
        props: {
          type: 'password',
          label: 'Label',
          error: true,
          errorMessage: 'This is an error message',
        },
      },
      {
        it: 'Password with error and helperText',
        props: {
          type: 'password',
          label: 'Label',
          error: true,
          errorMessage: 'This is an error message',
          helperText: 'Helper text',
        },
      },
      {
        it: 'Password with helperText and style overrides',
        props: {
          type: 'password',
          label: 'Label',
          error: true,
          errorMessage: 'This is an error message',
          helperText: 'Helper text',
          className: classes.root,
        },
      },
      {
        it: 'Password required',
        props: {
          type: 'password',
          label: 'Label',
          required: true,
        },
      },
      {
        it: 'Visible password',
        props: {
          type: 'password',
          label: 'Label',
          required: true,
          value: '123456',
          visible: true,
        },
      },
      {
        it: 'Password with bottom line theme',
        props: {
          type: 'password',
          theme: TextFieldTheme.Line,
        },
      },
      {
        it: 'Password huge with bottom line theme',
        props: {
          type: 'password',
          theme: TextFieldTheme.Line,
          className: classes.hugeText,
        },
      },
      {
        it: 'RTL',
        props: {
          dir: 'rtl',
          label: 'Amount',
          required: true,
          prefix: '$',
          suffix: <LikeHeart />,
          helperText: 'Money',
        },
      },
      {
        it: 'Floating label',
        props: {
          labelStyle: TextFieldLabelStyle.Floating,
          label: 'label',
        },
      },
      {
        it: 'Floating label with prefix icon',
        props: {
          labelStyle: TextFieldLabelStyle.Floating,
          label: 'label',
          prefix: <LikeHeart />,
        },
      },
      {
        it: 'Floating label with prefix symbol',
        props: {
          labelStyle: TextFieldLabelStyle.Floating,
          label: 'label',
          prefix: '$',
        },
      },
      {
        it: 'Floating label with value',
        props: {
          labelStyle: TextFieldLabelStyle.Floating,
          label: 'label',
          value: 'value',
        },
      },
      {
        it: 'Readonly',
        props: {
          readOnly: true,
          label: 'label',
          prefixLabel: 'prefix',
          prefix: <LikeHeart />,
          value: 'value',
        },
      },
      {
        it: 'Readonly with style override',
        props: {
          label: 'label',
          value: 'value',
          prefix: <LikeHeart />,
          readOnly: true,
          className: classes.readOnly,
        },
      },
      {
        it: 'ReadOnlyCustom',
        props: {
          label: 'label',
          value: 'value',
          prefix: <LikeHeart />,
          readOnlyCustom: true,
        },
      },
      {
        it: 'ReadOnlyCustom input with style override',
        props: {
          label: 'label',
          value: 'value',
          prefix: <LikeHeart />,
          readOnlyCustom: true,
          className: classes.readOnly,
        },
      },
      {
        it: 'input with padding overrides',
        props: {
          label: 'label',
          value:
            'Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.',
          className: classes.paddingOverrides,
        },
      },
      {
        it: 'input with spacing between text and icons overrides',
        props: {
          label: 'label',
          value:
            'Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.',
          prefix: <LikeHeart />,
          suffix: <Calendar />,
          className: classes.textIconSpacingOverrides,
        },
      },
      {
        it: 'number input with arrows suffix overrides',
        props: {
          type: 'number',
          label: 'label',
          className: classes.arrowsSuffixPaddingOverrides,
        },
      },
    ],
  },
];

tests.forEach(({ describe, its }) => {
  its.forEach(({ it, props }) => {
    storiesOf(`TextField/${describe}`, module).add(it, () => (
      <TextFieldVisual {...props} />
    ));
  });
});

function snapTest({
  theme,
  mouseAction,
  hover,
  focus,
  dir,
  success = false,
  error = false,
  suffix = null,
  withClearButton = false,
  label = '',
  maxLength = null,
  showCharCount = false,
  forceLabel = false,
  prefix = null,
  prefixLabel = null,
  type = null,
  className = null,
  labelStyle = TextFieldLabelStyle.Regular,
}) {
  snap(
    `${theme}${success ? '/success' : ''}${error ? '/error' : ''}
    ${suffix ? '/suffix' : ''}${withClearButton ? '/withClearButton' : ''}
    ${showCharCount && maxLength ? '/withCharCount' : ''}
    ${prefix ? '/withPrefix' : ''}${prefixLabel ? '/withPrefixLabel' : ''}${
      label ? '/withLabel' : ''
    }/${mouseAction}${className ? '/withOverrides' : ''}
    /${
      labelStyle === TextFieldLabelStyle.Floating ? '/withFloatingLabel' : ''
    }`.replace(/\s/g, ''),
    (done) => (
      <TextFieldAsyncVisual onDone={done} hover={hover} focus={focus} dir={dir}>
        <TextField
          theme={theme}
          success={success}
          successIcon={success}
          error={error}
          errorMessage="This is an error message"
          value="Some value"
          suffix={suffix}
          withClearButton={withClearButton}
          label={label}
          maxLength={maxLength}
          showCharCount={showCharCount}
          forceLabel={forceLabel}
          prefix={prefix}
          prefixLabel={prefixLabel}
          labelStyle={labelStyle}
          type={type}
          className={className}
        />
      </TextFieldAsyncVisual>
    ),
  );
}

visualize('TextField', () => {
  ['ltr', 'rtl'].forEach((dir: 'ltr' | 'rtl') => {
    story(dir, () => {
      Object.values(TextFieldTheme).forEach((theme) => {
        ['hover', 'focus'].forEach((mouseAction) => {
          const hover = mouseAction === 'hover';
          const focus = mouseAction === 'focus';

          snapTest({ theme, mouseAction, focus, hover, dir });
          snapTest({
            theme,
            mouseAction,
            focus,
            hover,
            dir,
            success: true,
          });
          snapTest({
            theme,
            mouseAction,
            focus,
            hover,
            dir,
            error: true,
          });
          snapTest({
            theme,
            mouseAction,
            focus,
            hover,
            dir,
            suffix: <Calendar />,
          });
          snapTest({
            theme,
            mouseAction,
            focus,
            hover,
            dir,
            withClearButton: true,
          });
          snapTest({
            theme,
            mouseAction,
            focus,
            hover,
            dir,
            suffix: <Calendar />,
            withClearButton: true,
          });
          snapTest({
            theme,
            mouseAction,
            focus,
            hover,
            dir,
            error: true,
            suffix: <Calendar />,
            withClearButton: true,
          });
          snapTest({
            theme,
            mouseAction,
            focus,
            hover,
            dir,
            label: 'label',
          });
          snapTest({
            theme,
            mouseAction,
            focus,
            hover,
            dir,
            maxLength: 5,
            showCharCount: true,
          });
          snapTest({
            theme,
            mouseAction,
            focus,
            hover,
            dir,
            prefix: '$',
            prefixLabel: 'USD',
          });
          snapTest({
            theme,
            mouseAction,
            focus,
            hover,
            dir,
            className: classes.hoverOverrides,
          });
          snapTest({
            theme,
            mouseAction,
            focus,
            hover,
            dir,
            label: 'label',
            labelStyle: TextFieldLabelStyle.Floating,
          });
          snapTest({
            theme,
            mouseAction,
            focus,
            hover,
            dir,
            labelStyle: TextFieldLabelStyle.Floating,
            prefix: '$',
            prefixLabel: 'USD',
          });
        });
      });
    });
  });
});

interface MultiStateTextFieldProps {
  variables?: Record<string, string>;
}

const MultiStateTextField: React.FC<MultiStateTextFieldProps> = ({
  variables: cssVariables = {},
}) => (
  <div
    style={{
      display: 'flex',
      flexDirection: 'row',
      gap: '20px',
      ...(cssVariables as React.CSSProperties),
    }}
  >
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
      <TextField
        theme={TextFieldTheme.Line}
        value="Normal state"
        label="Normal TextField"
        placeholder="Enter text here"
        showCharCount
        maxLength={20}
      />

      <TextField
        theme={TextFieldTheme.Line}
        value="123.45"
        label="Amount"
        prefix="$"
        suffix={<Calendar />}
        prefixLabel="USD"
        helperText="Helper text with custom styling"
      />

      <TextField
        theme={TextFieldTheme.Line}
        value="Invalid input"
        label="Error State"
        error
        errorMessage="This field has an error with custom styling"
        helperText="Helper text shown with error"
      />

      <TextField
        theme={TextFieldTheme.Line}
        value="Disabled field"
        label="Disabled State"
        suffix={<Calendar />}
        disabled
        placeholder="This field is disabled"
        prefix="$"
      />

      <TextField
        theme={TextFieldTheme.Line}
        value="Valid input"
        label="Success State"
        success
        successIcon
        helperText="This field is valid"
      />

      <TextField
        theme={TextFieldTheme.Line}
        type="password"
        value="password123"
        label="Password Field"
        visible={true}
        helperText="Password is visible"
      />

      <TextField
        theme={TextFieldTheme.Line}
        value=""
        label="Empty Field"
        placeholder="Custom placeholder styling"
      />
    </div>

    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
      <TextField
        theme={TextFieldTheme.Box}
        value="Normal state"
        label="Normal TextField (Box Theme)"
        placeholder="Enter text here"
        showCharCount
        maxLength={20}
      />

      <TextField
        theme={TextFieldTheme.Box}
        value="123.45"
        label="Amount (Box Theme)"
        prefix="$"
        suffix={<Calendar />}
        prefixLabel="USD"
        helperText="Helper text with custom styling"
      />

      <TextField
        theme={TextFieldTheme.Box}
        value="Invalid input"
        label="Error State (Box Theme)"
        error
        errorMessage="This field has an error with custom styling"
        helperText="Helper text shown with error"
      />

      <TextField
        theme={TextFieldTheme.Box}
        value="Disabled field"
        label="Disabled State (Box Theme)"
        disabled
        suffix={<Calendar />}
        placeholder="This field is disabled"
        prefix="$"
      />

      <TextField
        theme={TextFieldTheme.Box}
        value="Valid input"
        label="Success State (Box Theme)"
        success
        successIcon
        helperText="This field is valid"
      />

      <TextField
        theme={TextFieldTheme.Box}
        type="password"
        value="password123"
        label="Password Field (Box Theme)"
        visible={true}
        helperText="Password is visible"
      />

      <TextField
        theme={TextFieldTheme.Box}
        value=""
        label="Empty Field (Box Theme)"
        placeholder="Custom placeholder styling"
      />
    </div>
  </div>
);

visualize('TextField CSS Variables', () => {
  story('CSS Variable Overrides', () => {
    snap(
      'primary background',
      <MultiStateTextField
        variables={{
          '--wst-primary-background-color-rgb': '0,255,0',
        }}
      />,
    );

    snap(
      'shade 3',
      <MultiStateTextField
        variables={{
          '--wst-shade-3-color-rgb': '0,255,0',
        }}
      />,
    );

    snap(
      'paragraph 2 color',
      <MultiStateTextField
        variables={{
          '--wst-paragraph-2-color-rgb': '0,255,0',
          '--wst-paragraph-2-font-family': 'Libre Baskerville',
          '--wst-paragraph-2-font-size': '20px',
        }}
      />,
    );

    snap(
      'system disabled color',
      <MultiStateTextField
        variables={{
          '--wst-system-disabled-color-rgb': '0,255,0',
        }}
      />,
    );

    snap(
      'error color',
      <MultiStateTextField
        variables={{
          '--wst-system-error-color-rgb': '0,255,0',
        }}
      />,
    );

    snap(
      'success color',
      <MultiStateTextField
        variables={{
          '--wst-system-success-color-rgb': '255,0,255',
        }}
      />,
    );
  });
});
